import React, { useState, useEffect } from 'react';
import axios from 'axios';

const initialFormData = {
  table_name: '',
  template_name: '',
  header_image: null,
  header_type: '',
  is_header_static: 0,
  is_header_id: 1,
  is_header_required: 1, // Updated default: 1 means "No" per your new mapping
  hcolumn: '',
  wid: '',
  user_id: '',
  pid: '',
  lang: '',
};

export default function EbotConfigManager() {
  const [formData, setFormData] = useState(initialFormData);
  const [configs, setConfigs] = useState([]);
  const [languagePosts, setLanguagePosts] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);

  const [waId, setWaId] = useState(null);
  const [pid, setPid] = useState(null);

  // 🔐 Added loading state to prevent duplicate/double submissions
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const storedUser = sessionStorage.getItem('user');

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const isAdmin = parsedUser.role === "admin";
        const finalUserId = isAdmin ? parsedUser.id : parsedUser.userId;

        setFormData((prev) => ({
          ...prev,
          wid: parsedUser.wa_id || '',
          user_id: finalUserId || '',
          pid: parsedUser.p_id || '',
        }));

      } catch (e) {
        console.error('Error parsing user from sessionStorage:', e);
      }
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, []);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    let parsedUser = null;

    if (storedUser) {
      try {
        parsedUser = JSON.parse(storedUser);
      } catch (e) {
        console.error("Invalid session user:", e);
      }
    }

    const finalUserId = parsedUser?.role === "admin" ? formData.user_id : parsedUser?.userId || "";

    if (finalUserId && waId) {
      fetchLanguagePosts(finalUserId, waId);
    }
  }, [formData.lang, formData.user_id, waId]);

  const fetchLanguagePosts = async (user_id, waId) => {
    try {
      const response = await axios.get(
        "https://botapi.emedha.in/pages/api/api/language-get-all",
        { params: { user_id, wa_id: waId } }
      );
      setLanguagePosts(response.data);
    } catch (error) {
      console.error("Failed to fetch language posts:", error);
    }
  };

  useEffect(() => {
    if (formData.user_id) {
      fetchTableOptions(formData.user_id);
    }
  }, [formData.user_id]);

  const fetchTableOptions = async (userId) => {
    try {
      const storedUser = JSON.parse(sessionStorage.getItem('user'));
      if (!storedUser) return;

      const response = await axios.get('https://botapi.emedha.in/pages/api/api/tables', {
        params: { userId }
      });

      const tables = response.data;
      let filteredTables;

      if (storedUser.role === "admin") {
        filteredTables = tables;
      } else if (storedUser.role === "member") {
        filteredTables = tables.filter(
          (t) => t.role === "member" && t.member_id === storedUser.id
        );
      }

      setTableOptions(filteredTables);
    } catch (err) {
      console.error("Failed to fetch tables:", err);
    }
  };

  const fetchConfigs = async () => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      if (!user) return;
      const res = await axios.get(`https://botapi.emedha.in/pages/api/ebot-configs/${user.id}`);
      setConfigs(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const loggedUser = JSON.parse(sessionStorage.getItem("user"));
    if (!loggedUser) return;

    const emailToUse = loggedUser.role === "admin" ? loggedUser.email : loggedUser.adminEmail;
    if (!emailToUse) return;

    axios.get(`https://botapi.emedha.in/pages/api/get-user-data?email=${encodeURIComponent(emailToUse)}`)
      .then((response) => {
        const userData = response.data;
        if (userData.wa_id) {
          setWaId(userData.wa_id);
          setPid(userData.p_id);
        }
      })
      .catch((err) => {
        console.error("Error retrieving user data:", err);
      });
  }, []);

  useEffect(() => {
    if (!waId) return;
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`https://emedha.com/echatbot/enotifier/getTemplateStatus.php?limit=10&wid=${waId}`);
        const json = await response.json();
        const approvedTemplates = json.data?.filter((item) => item.status === "APPROVED") || [];
        setTemplateOptions(approvedTemplates);
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      }
    };
    fetchTemplates();
  }, [waId]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'radio' ? parseInt(value) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const type = formData.header_type;
    const fileName = file.name.toLowerCase();

    const isValid =
      (type === 'image' && /\.(jpg|jpeg|png)$/.test(fileName)) ||
      (type === 'pdf' && /\.pdf$/.test(fileName)) ||
      (type === 'video' && /\.(mp4|mov|avi|mkv)$/.test(fileName));

    if (!isValid) {
      alert(`Invalid file type for selected header type: ${type.toUpperCase()}`);
      e.target.value = '';
      return;
    }

    setFormData((prev) => ({ ...prev, header_image: file }));
  };

  const [selectedColumns, setSelectedColumns] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // 🛑 Duplicate Check Added Here
    const isDuplicate = configs.some(
      (config) =>
        config.table_name === formData.table_name &&
        config.template_name === formData.template_name
    );

    if (isDuplicate) {
      alert(`Duplicate Error: The table "${formData.table_name}" is already mapped to the template "${formData.template_name}". Each table can only be mapped to a template once.`);
      return;
    }

    try {
      setIsSubmitting(true);
      const storedUser = sessionStorage.getItem("user");
      const parsedUser = storedUser ? JSON.parse(storedUser) : {};
      const adminId = parsedUser.role === "admin" ? parsedUser.id : parsedUser.userId;
      const memberId = parsedUser.role === "member" ? parsedUser.id : 0;
      const finalFormData = {
        ...formData,
        wid: waId,
        user_id: adminId,
        pid: pid,
        tcolumn: selectedColumns.join(','),
        role: parsedUser.role,
        member_id: memberId,
      };

      const data = new FormData();
      for (let key in finalFormData) {
        if (finalFormData[key] !== null) data.append(key, finalFormData[key]);
      }

      await axios.post('https://botapi.emedha.in/pages/api/ebot_submit', data);
      alert("Template created successfully!");

      setFormData(initialFormData);
      setSelectedColumns([]);
      fetchConfigs();
    } catch (err) {
      console.error(err);
      alert("An error occurred while submitting the template.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const [tableColumns, setTableColumns] = useState([]);

  useEffect(() => {
    const fetchTableColumns = async () => {
      if (!formData.table_name || !formData.user_id) return;
      try {
        const response = await axios.get('https://botapi.emedha.in/pages/api/api/get-table-columns', {
          params: { tableName: formData.table_name, userId: formData.user_id }
        });
        const sortedColumns = response.data.sort((a, b) => {
          if (a.COLUMN_NAME.toLowerCase() === 'name') return -1;
          if (b.COLUMN_NAME.toLowerCase() === 'name') return 1;
          return a.COLUMN_NAME.localeCompare(b.COLUMN_NAME);
        });
        setTableColumns(sortedColumns);
      } catch (err) {
        console.error("Failed to fetch table columns:", err);
        setTableColumns([]);
      }
    };
    fetchTableColumns();
  }, [formData.table_name, formData.user_id]);

  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0", backgroundColor: "#ffffff", fontSize: "14px", color: "#0f172a", outline: "none", boxSizing: "border-box", transition: "all 0.2s" };
  const labelStyle = { fontSize: "12px", fontWeight: "600", color: "#0f172a", display: "block", marginBottom: "6px" };

  return (
    <div style={{ fontFamily: "sans-serif", backgroundColor: "#f8fafc", minHeight: "100vh", color: "#0f172a" }}>
      <div style={{ marginBottom: "24px", borderBottom: "1px solid #e2e8f0", paddingBottom: "16px", maxWidth: "1200px", margin: "0 auto 24px auto" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "700", margin: "0 0 4px 0", color: "#0f172a" }}>Template Mapping</h1>
        <p style={{ fontSize: "14px", color: "#64748b", margin: 0 }}>Map database tables and columns onto approved WhatsApp templates</p>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", justifyContent: "center", maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ flex: "1 1 500px", minWidth: "320px" }}>
          <form onSubmit={handleSubmit} style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column", gap: "16px" }}>
            <h2 style={{ fontSize: "16px", fontWeight: "600", margin: "0 0 4px 0", borderBottom: "1px solid #f1f5f9", paddingBottom: "12px" }}>Create Template Mapping</h2>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={labelStyle}>Template Name <span style={{ color: "#ef4444" }}>*</span></label>
              <select name="template_name" value={formData.template_name}
                onChange={(e) => {
                  const selectedName = e.target.value;
                  const selected = templateOptions.find(t => t.name === selectedName);
                  setFormData(prev => ({
                    ...prev,
                    template_name: selectedName,
                    wid: selected?.wa_id || '',
                    pid: selected?.p_id || ''
                  }));
                }} required style={inputStyle}>
                <option value="">Select Template</option>
                {templateOptions.map((tpl) => (
                  <option key={tpl.id} value={tpl.name}>{tpl.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={labelStyle}>Table Name <span style={{ color: "#ef4444" }}>*</span></label>
              <select name="table_name" value={formData.table_name} onChange={handleInputChange} required style={inputStyle}>
                <option value="">Select Table</option>
                {tableOptions.map((table) => (
                  <option key={table.sno} value={table.table_name}>{table.table_name}</option>
                ))}
              </select>
            </div>


            {/* ── Is Header Required Option (no-1, yes-0) ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "8px" }}>
              <span style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Is Header Required? <span style={{ color: "#ef4444" }}>*</span></span>
              <div style={{ display: 'flex', gap: '16px', padding: '4px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: "500" }}>
                  <input type="radio" name="is_header_required" value={0} checked={formData.is_header_required === 0} onChange={handleInputChange} style={{ accentColor: '#3b82f6' }} /> Yes
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: "500" }}>
                  <input type="radio" name="is_header_required" value={1} checked={formData.is_header_required === 1} onChange={handleInputChange} style={{ accentColor: '#3b82f6' }} /> No
                </label>
              </div>
            </div>

            {/* ── Wrap Header Type & Configuration blocks to only display if is_header_required === 0 (Yes) ── */}
            {formData.is_header_required === 0 && (
              <>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={labelStyle}>Header Type</label>
                  <select name="header_type" value={formData.header_type} onChange={handleInputChange} style={inputStyle}>
                    <option value="">Select Type</option>
                    <option value="image">Image</option>
                    <option value="pdf">Document</option>
                    <option value="video">Video</option>
                  </select>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", paddingTop: "16px", borderTop: "1px solid #f1f5f9" }}>
                  <div>
                    <span style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Header Static?</span>
                    <div style={{ display: 'flex', gap: '16px', padding: '8px 0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: "500" }}>
                        <input type="radio" name="is_header_static" value={0} checked={formData.is_header_static === 0} onChange={handleInputChange} style={{ accentColor: '#3b82f6' }} />Static
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: "500" }}>
                        <input type="radio" name="is_header_static" value={1} checked={formData.is_header_static === 1} onChange={handleInputChange} style={{ accentColor: '#3b82f6' }} />Dynamic
                      </label>
                    </div>
                  </div>

                  <div>
                    <span style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Is Header ID? (no-0, yes-1)</span>
                    <div style={{ display: 'flex', gap: '16px', padding: '8px 0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: "500" }}>
                        <input type="radio" name="is_header_id" value={1} checked={formData.is_header_id === 1} onChange={handleInputChange} style={{ accentColor: '#3b82f6' }} />Yes</label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: "500" }}>
                        <input type="radio" name="is_header_id" value={0} checked={formData.is_header_id === 0} onChange={handleInputChange} style={{ accentColor: '#3b82f6' }} />No</label>
                    </div>
                  </div>
                </div>

                {formData.is_header_static === 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: "12px", borderTop: "1px solid #f1f5f9" }}>
                    <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>
                      {formData.is_header_id === 0 ? 'Header File' : 'Header ID Number Value'}
                    </label>
                    {formData.is_header_id === 0 ? (
                      <input type="file" name="header_image" accept={formData.header_type === 'image' ? 'image/jpeg,image/png' : formData.header_type === 'pdf' ? '.pdf' : 'video/mp4,video/mov,video/avi,video/mkv'} onChange={handleFileChange} style={{ fontSize: "13px", color: "#64748b", padding: "6px 0" }} />
                    ) : (
                      <input type="number" name="header_image" value={formData.header_image || ''} onChange={handleInputChange} placeholder="Enter numerical ID value" style={{ ...inputStyle, padding: "8px 12px", fontSize: "13px" }} />
                    )}
                  </div>
                )}
                {formData.is_header_static === 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingTop: "12px", borderTop: "1px solid #f1f5f9" }}>
                    <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Header Column</label>
                    <select name="hcolumn" value={formData.hcolumn} onChange={handleInputChange} style={inputStyle}>
                      <option value="">Select Header Column</option>
                      {tableColumns.map((col, idx) => (
                        <option key={idx} value={col.COLUMN_NAME}>{col.COLUMN_NAME}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}

            {/* Language Select */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={labelStyle}>Language</label>
              <select name="lang" value={formData.lang} onChange={handleInputChange} style={inputStyle}>
                <option value="">Select Language</option>
                {languagePosts.map((post) => (
                  <option key={post.id || post.language} value={post.language}>{post.language}</option>
                ))}
              </select>
            </div>

            <button type="submit" disabled={isSubmitting} style={{ padding: '12px 20px', backgroundColor: isSubmitting ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', marginTop: '12px', boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)", transition: "background-color 0.2s" }}>
              {isSubmitting ? "Creating..." : "Create Template"}
            </button>
          </form>
        </div>

        {/* ── Right Column: Interactive Variables Workstation Grid Column ── */}
        <div style={{ flex: "1 1 500px", minWidth: "320px" }}>
          {tableColumns.length > 0 ? (
            <div style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>

              <div style={{ paddingBottom: "12px", marginBottom: "16px", borderBottom: "1px solid #e2e8f0" }}>
                <h6 style={{ fontSize: "12px", fontWeight: "700", textTransform: "uppercase", margin: 0 }}>Table Schema</h6>
                <p style={{ fontSize: "11px", color: "#64748b", margin: "4px 0 0 0" }}>Select variables to include in the mapping workflow</p>
              </div>

              <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>
                      <th style={{ padding: '12px 10px' }}>Column Name</th>
                      <th style={{ padding: '12px 10px' }}>Type</th>
                      <th style={{ padding: '12px 10px' }}>Description</th>
                      <th style={{ padding: '12px 10px', textAlign: 'center', width: "64px" }}>Bind</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableColumns.map((col, idx) => {
                      const protectedColumns = ['user_id', 'member_id', 'role', 'flag', 'subgroup_id', 'group_id', 'mobile', 'id'];
                      const isDisabled = protectedColumns.includes(col.COLUMN_NAME.toLowerCase());
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: isDisabled ? "#f8fafc" : "transparent", opacity: isDisabled ? 0.6 : 1 }}>
                          <td style={{ padding: '12px 10px', fontWeight: '600', color: '#0f172a' }}>{col.COLUMN_NAME}</td>
                          <td style={{ padding: '12px 10px' }}>
                            <code style={{ background: '#eff6ff', color: '#2563eb', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontFamily: "monospace" }}>
                              {col.COLUMN_TYPE}
                            </code>
                          </td>
                          <td style={{ padding: '12px 10px', color: '#64748b', fontSize: '12px' }}>
                            {col.COLUMN_COMMENT || <em style={{ color: '#94a3b8' }}>No description</em>}
                          </td>
                          <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                            <input type="checkbox" name={`col_${idx}`} disabled={isDisabled} checked={selectedColumns.includes(col.COLUMN_NAME)}
                              style={{ width: '16px', height: '16px', accentColor: '#3b82f6', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedColumns((prev) =>
                                  checked ? [...prev, col.COLUMN_NAME] : prev.filter((c) => c !== col.COLUMN_NAME)
                                );
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {selectedColumns.length > 0 && (
                <p style={{ fontSize: "12px", color: "#64748b", marginTop: "16px" }}>
                  <span style={{ fontWeight: "600", color: "#0f172a" }}>{selectedColumns.length}</span> column{selectedColumns.length !== 1 ? "s" : ""} selected:{" "}
                  <span style={{ fontFamily: "monospace", color: "#2563eb", fontWeight: "600" }}>{selectedColumns.join(", ")}</span>
                </p>
              )}
            </div>
          ) : (
            <div style={{ backgroundColor: "rgba(255,255,255,0.4)", border: "2px dashed #e2e8f0", borderRadius: "12px", padding: "48px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px" }}>
              <div style={{ width: "40px", height: "40px", borderRadius: "50%", backgroundColor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "18px", fontWeight: "bold" }}>⚡</div>
              <p style={{ fontSize: "13px", fontWeight: "500", color: "#64748b", margin: 0 }}>Select a table layout configurations above to view schema tracking columns.</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}